import { Form, DatePicker, Divider, Modal, Input, Button, Card } from 'antd'
import React, { useEffect, useState } from 'react'
import { ICreateTaxYearRes, IPeriodCache } from '../../lib/interfaces'
import { User } from '../../types/session'
import './style.less'
import { httpPost } from '../../services/http'
import moment from 'moment'
import { isInTaxYear, renderDateRange, renderDayCount } from './helpers'

const { YearPicker } = DatePicker

export default function CreateReport({
    llPeriods,
    userData,
    loadOverviewData,
}: {
    llPeriods: IPeriodCache[]
    userData: User | null,
    loadOverviewData: (tab?: string) => void
}) {
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(false)
    const [taxYear, setTaxYear] = useState<moment.Moment>(null)
    const [reportName, setReportName] = useState(
        `Ligningsår${taxYear ? ' ' + taxYear.toDate().getFullYear() : ''}`
    )

    useEffect(() => {
        setReportName(`Ligningsår${taxYear ? ' ' + taxYear.toDate().getFullYear() : ''}`)
    }, [taxYear])

    const error = (message: string) => {
        Modal.error({
            title: 'Something went wrong',
            content: message,
        })
    }

    const onFinish = async (values: any) => {
        setLoading(true)
        try {
            const res = await httpPost<ICreateTaxYearRes>('/ll-tax-years/', {
                ll_tax_year: values['range-picker'].toDate().getFullYear(),
                tax_year_name: values.reportName,
                person_id: values.personId,
                full_name: values.fullName,
            })
            await loadOverviewData(`taxYear;;;${res.data.id}`)
        } catch (err) {
            if (err.status === 400) {
                error(err.data?.detail)
            } else {
                error(err.message)
            }
            form.resetFields()
        } finally {
            setLoading(false)
        }
    }

    function testIsInTaxYear(llPeriod: IPeriodCache) {
        if (!taxYear) return false
        return isInTaxYear(llPeriod, taxYear.toDate().getFullYear())
    }

    return <>
        <div className="report-tab-header">
            <h1>Specify tax year</h1>
            <Divider />
        </div>
        <div className="create-report-container">
            <Card
                title="Enter your information"
                bordered={false}
                className="create-report-card"
            >
                <Form onFinish={onFinish} layout="inline">
                    <div>
                        <Form.Item
                            // hasFeedback
                            name="fullName"
                            rules={[{ required: true, message: 'Please enter your full name' }]}
                            className="create-report-input"
                            label="Full name"
                        >
                            <Input
                                // prefix={<MailOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                                placeholder="Legal name"
                                className="create-report-input"
                            />
                        </Form.Item>
                        <Form.Item
                            hasFeedback
                            name="personId"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter your CPR number',
                                },
                                {
                                    pattern: /^\d{6}-\d{4}$/,
                                    message: 'invalid CPR',
                                },
                            ]}
                            className="create-report-multi-input"
                            label="CPR number"
                        >
                            <Input placeholder="010101-1234" className="create-report-input" />
                        </Form.Item>
                    </div>
                    <Form.Item
                        name="range-picker"
                        label="Tax year"
                        className="create-report-input"
                        rules={[{ required: true, message: 'Provide a tax year' }]}
                    >
                        <YearPicker
                            value={taxYear}
                            onChange={(v) => {
                                setTaxYear(v)
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        // hasFeedback
                        name="reportName"
                        rules={[{ required: true, message: 'Please provide a name' }]}
                        className="create-report-input"
                        label="Report name"
                    >
                        <Input
                            // prefix={<MailOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                            placeholder="Fx Ligningsår 2020"
                            className="create-report-input"
                        />
                    </Form.Item>
                    <Form.Item>
                        <Button
                            className="landing-button"
                            htmlType="submit"
                            type="primary"
                            loading={loading}
                        >
                            Create tax year
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
            <div className="create-report-example-cards-container">
                <Card
                    title={`Exemption periods regarding ${
                        taxYear?.toDate()?.getFullYear() || '--'
                    }:`}
                    bordered={false}
                >
                    {llPeriods
                        .filter((p) => testIsInTaxYear(p))
                        .map((p) => (
                            <p>
                                {`${renderDateRange(p.start, p.end)} - max daycount: `}{' '}
                                {renderDayCount(p.max_day_count)}
                            </p>
                        ))}
                </Card>
                <Card
                    title={
                        'A Report for relevant dates in each exemption period will be created:​'
                    }
                    bordered={false}
                >
                    {llPeriods
                        .filter((p) => testIsInTaxYear(p))
                        .map((p, idx) => (
                            <p>{`${reportName} - ${idx + 1}`}</p>
                        ))}
                </Card>
            </div>
            <div className="report-tab-header">
                <p>
                    Remember: you can still change the exemption periods after you create a tax
                    year.
                </p>
            </div>
        </div>
    </>;
}
